import axios from '@/axios.config.js';
import {hmToSecondsOnly, secondsToHm, secondsToHms} from "@/helpers";

class ExamService {
    systemToAPIExam(entity) {
        return {
            name: entity.name,
            klass_id: entity.klass_id,
            status: entity.status,
            type: entity.type,
            description: entity.description,
            show_user_details_to_teacher: entity.show_user_details_to_teacher,
            allow_return_to_previous_question: entity.allow_return_to_previous_question,
            attachment_description: entity.attachment_description,
            attachment_file: entity.attachment_file,
            start_at: entity.start_at,
            show_results_at: entity.show_results_at,
            maximum_entry_time: hmToSecondsOnly(entity.maximum_entry_time),
            duration: hmToSecondsOnly(entity.duration),
        }
    }

    APIToSystemExam(entity) {
        const newEntity = Object.assign({}, entity)
        newEntity.duration = secondsToHm(entity.duration)
        newEntity.maximum_entry_time = secondsToHm(entity.maximum_entry_time)
        return newEntity
    }

    APIToSystemExamResult(entity) {
        const newEntity = Object.assign({}, entity)

        for (const question of newEntity.questions.values()) {
            question.duration = secondsToHms(question.duration)
        }

        newEntity.exam.duration = secondsToHm(newEntity.exam.duration)

        return newEntity
    }

    APIToSystemExamInteraction(entity) {
        const newEntity = Object.assign({}, entity)

        for (const question of newEntity.questions.values()) {
            question.duration = secondsToHms(question.duration)
        }

        newEntity.exam.duration = secondsToHm(newEntity.exam.duration)

        return newEntity
    }

    APIToSystemExamParticipated(entity) {
        const newEntity = Object.assign({}, entity)

        return newEntity
    }

    systemToAPILabel(entity) {
        return {
            name: entity.name,
            order: entity.order ?? 0,
            duration: hmToSecondsOnly(entity.duration),
            randomize_questions: entity.randomize_questions === "1",
        }
    }

    APIToSystemLabel(entity) {
        const newEntity = Object.assign({}, entity)
        newEntity.duration = secondsToHm(entity.duration)
        newEntity.randomize_questions = entity.randomize_questions ? "1" : "0"
        return newEntity
    }


    systemToAPILabelLimitation(entity) {
        return {
            question_count_limit: entity.question_count_limit,
            question_ids: entity.question_ids,
        }
    }

    APIToSystemLabelLimitation(entity) {
        const newEntity = Object.assign({}, entity)
        return newEntity
    }

    systemToAPIQuestion(entity) {
        return {
            label_id: entity.label_id,
            content: entity.content,
            has_intraction: entity.has_intraction,
            time_left: entity.time_left,
            order: entity.order ?? 0,
            type: entity.type,
            score: entity.score,
            negative_score: entity.negative_score,
            show_score: entity.show_score,
            explanation: entity.explanation,
            required_detail_answer: entity.required_detail_answer,
            required_answer: entity.required_answer,
            randomize_options: entity.randomize_entitys,
            multi_media: entity.multi_media,
            answer_multi_media: entity.answer_multi_media,
            duration: entity.duration,
            options: entity.options,
        }
    }

    systemToAPIExamResult(entity) {
        return {
            id: entity.id,
            first_name: entity.user?.first_name,
            last_name: entity.user?.last_name,
            national_id: entity.user?.national_id,
            status: entity.status,
        }
    }

    copyExam(cancelToken, examId) {
        return axios
            .post(`exams/${examId}/!copy`, {}, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExam(response.data);
            });
    }

    createExam(cancelToken, exam) {
        return axios
            .post('exams', this.systemToAPIExam(exam), {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExam(response.data);
            });
    }

    updateExam(cancelToken, exam) {
        return axios
            .put(`exams/${exam.id}`, this.systemToAPIExam(exam), {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExam(response.data);
            });
    }

    deleteExam(cancelToken, examId) {
        return axios
            .delete(`exams/${examId}`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExam(response.data);
            });
    }

    getExam(cancelToken, examId) {
        return axios
            .get(`exams/${examId}`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExam(response.data);
            });
    }

    listExams(cancelToken) {
        return axios
            .get('exams', {cancelToken: cancelToken.token})
            .then(response => {
                return response.data.map((exam) => this.APIToSystemExam(exam))
            });
    }

    listResultsExam(cancelToken, examId) {
        return axios
            .get(`exams/${examId}/interactions`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data.map((examResult) => this.systemToAPIExamResult(examResult))
            });
    }

    getResultExam(cancelToken, examId, resultId) {
        return axios
            .get(`exams/${examId}/interactions/${resultId}/score`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExamResult(response.data);
            });
    }

    getStudentResultExam(cancelToken, examId) {
        return axios
            .get(`exams/${examId}/participated/interactions/score`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExamResult(response.data);
            });
    }

    listParticipatedExam(cancelToken) {
        return axios
            .get(`/exams/participated`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data.map((examResult) => this.APIToSystemExamParticipated(examResult))
            });
    }

    getInteractionExam(cancelToken, examId) {
        return axios
            .get(`exams/${examId}/participated/interactions`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemExamInteraction(response.data);
            });
    }

    listAvailableExams(cancelToken) {
        return axios
            .get('available-exams', {cancelToken: cancelToken.token})
            .then(response => {
                return response.data.map((exam) => this.APIToSystemExam(exam))
            });
    }

    listAllowedStudentsForExam(cancelToken, examId) {
        return axios
            .get(`/exams/${examId}/students`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    syncAllowedStudentsForExam(cancelToken, examId, data) {
        return axios
            .post(`/exams/${examId}/students`, {students: data.students}, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    listAllowedTeachersForExam(cancelToken, examId) {
        return axios
            .get(`/exams/${examId}/teachers`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    syncAllowedTeachersForExam(cancelToken, examId, data) {
        return axios
            .post(`/exams/${examId}/teachers`, {teachers: data.teachers}, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    listLabels(cancelToken, examId) {
        return axios
            .get(`exams/${examId}/labels`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data.map((label) => this.APIToSystemLabel(label))
            });
    }

    createLabel(cancelToken, examId, label) {
        return axios
            .post(`exams/${examId}/labels`, this.systemToAPILabel(label), {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemLabel(response.data);
            });
    }

    updateLabel(cancelToken, examId, label) {
        return axios
            .put(`exams/${examId}/labels/${label.id}`, this.systemToAPILabel(label), {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemLabel(response.data);
            });
    }

    updateLabelLimitation(cancelToken, examId, label) {
        return axios
            .patch(`exams/${examId}/labels/${label.id}/limit`, this.systemToAPILabelLimitation(label), {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemLabelLimitation(response.data);
            });
    }

    deleteLabel(cancelToken, examId, labelId) {
        return axios
            .delete(`exams/${examId}/labels/${labelId}`, {cancelToken: cancelToken.token})
            .then(response => {
                return this.APIToSystemLabel(response.data);
            });
    }

    listQuestions(cancelToken, examId) {
        return axios
            .get(`exams/${examId}/questions`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    listQuestionsByLabelId(cancelToken, examId, labelId) {
        return axios
            .get(`exams/${examId}/labels/${labelId}/questions`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    createQuestion(cancelToken, examId, question) {
        return axios
            .post(`exams/${examId}/questions`, this.systemToAPIQuestion(question), {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    importQuestion(cancelToken, examId, fileId) {
        return axios
            .post(`exams/${examId}/questions/!import`, fileId, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    updateQuestion(cancelToken, examId, question) {
        return axios
            .put(`exams/${examId}/questions/${question.id}`, this.systemToAPIQuestion(question), {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    deleteQuestion(cancelToken, examId, questionId) {
        return axios
            .delete(`exams/${examId}/questions/${questionId}`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data;
            });
    }

    examInteractionsReport(cancelToken, examId) {
        return axios.get(`reports/exams/${examId}/participated`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    examAttendanceReport(cancelToken, examId) {
        return axios.get(`reports/exams/${examId}/attendance`, {cancelToken: cancelToken.token})
            .then(response => {
                return response.data
            })
    }

    submitUserScore(cancelToken, examId, interactionId, userScore) {
        return axios
            .post(`exams/${examId}/interactions/${interactionId}/questions/${userScore.questionId}/submit-score`, {user_score: userScore.score,}, {cancelToken: cancelToken.token})
            .then((response) => {
                return this.APIToSystemExamResult(response.data)
            })
    }

}

export default new ExamService();